/**
 * ShowGroom Local Database
 * 
 * IndexedDB-backed database using Dexie for fully local, serverless operation.
 * Supports offline usage, persistent storage, and backup/restore.
 */

import Dexie, { type Table } from 'dexie';

// ==================== Types ====================

export type Role = 'admin' | 'user';
export type BookingStatus = 'pending' | 'confirmed' | 'completed' | 'cancelled';
export type PetType = 'dog' | 'cat';

export interface User {
  id: string;
  username: string;
  email: string;
  passwordHash: string;
  role: Role;
  createdAt: string;
}

export interface Booking {
  id: string;
  name: string;
  phone: string;
  email?: string;
  petName?: string;
  petType?: PetType;
  service: string;
  date: string;
  time: string;
  notes?: string;
  status: BookingStatus;
  createdAt: string;
}

export interface ImageSEOData {
  filename_base?: string;
  primary_alt_he?: string;
  primary_alt_en?: string;
  title_he?: string;
  figcaption_he?: string;
  suggested_page?: string;
  keywords_he?: string[];
  keywords_en?: string[];
  breed_detected?: string;
  breed_confidence?: number;
  other_attributes?: string[];
  html_figure?: string;
  srcset_snippet?: string;
  json_ld_imageobject?: string;
  confidence_overall?: number;
  privacy_note?: string | null;
  analyzedAt?: string;
}

export interface GalleryImage {
  id: string;
  imageBlob?: Blob;
  imageUrl?: string;
  uploadedAt: string;
  seo?: ImageSEOData;
  description?: string;
  serviceType?: string;
}

/** @deprecated Use GalleryImage instead */
export type ImagePair = GalleryImage;

export interface BusinessHoursDay {
  isOpen: boolean;
  openTime: string;
  closeTime: string;
}

export interface BusinessHoursData {
  sunday: BusinessHoursDay;
  monday: BusinessHoursDay;
  tuesday: BusinessHoursDay;
  wednesday: BusinessHoursDay;
  thursday: BusinessHoursDay;
  friday: BusinessHoursDay;
  saturday: BusinessHoursDay;
  holidays?: string[];
  slotDuration?: number;
}

export interface BusinessHoursDoc {
  id: 'singleton';
  data: BusinessHoursData;
}

export interface SettingsDoc {
  id: 'singleton';
  migratedFromLocalStorage?: boolean;
  backupVersion?: number;
  lastBackup?: string;
}

// ==================== Database Class ====================

export class ShowGroomDB extends Dexie {
  users!: Table<User, string>;
  bookings!: Table<Booking, string>;
  images!: Table<GalleryImage, string>;
  businessHours!: Table<BusinessHoursDoc, string>;
  settings!: Table<SettingsDoc, string>;

  constructor() {
    super('showgroom');

    this.version(1).stores({
      users: 'id, &username, &email, role, createdAt',
      bookings: 'id, status, date, createdAt',
      images: 'id, uploadedAt',
      businessHours: 'id',
      settings: 'id'
    });

    // Migration: before/after image pairs → single gallery images
    this.version(2).stores({
      users: 'id, &username, &email, role, createdAt',
      bookings: 'id, status, date, createdAt',
      images: 'id, uploadedAt',
      businessHours: 'id',
      settings: 'id'
    }).upgrade(tx => {
      return tx.table('images').toCollection().modify((img: any) => {
        // Migrate: prefer afterBlob/afterUrl (the finished result), fall back to before
        if (img.afterBlob || img.beforeBlob) {
          img.imageBlob = img.afterBlob || img.beforeBlob;
        }
        if (img.afterUrl || img.beforeUrl) {
          img.imageUrl = img.afterUrl || img.beforeUrl;
        }
        // Remove old fields
        delete img.beforeBlob;
        delete img.afterBlob;
        delete img.beforeUrl;
        delete img.afterUrl;
      });
    });
  }
}

// ==================== Database Instance ====================

export const db = new ShowGroomDB();

// ==================== Default Values ====================

export const DEFAULT_BUSINESS_HOURS: BusinessHoursData = {
  sunday: { isOpen: true, openTime: '09:00', closeTime: '18:00' },
  monday: { isOpen: true, openTime: '09:00', closeTime: '18:00' },
  tuesday: { isOpen: true, openTime: '09:00', closeTime: '18:00' },
  wednesday: { isOpen: true, openTime: '09:00', closeTime: '18:00' },
  thursday: { isOpen: true, openTime: '09:00', closeTime: '18:00' },
  friday: { isOpen: true, openTime: '09:00', closeTime: '16:00' },
  saturday: { isOpen: false, openTime: '09:00', closeTime: '18:00' },
  holidays: [],
  slotDuration: 60,
};

/**
 * Default admin for LOCAL MODE ONLY (fallback when backend is not available).
 *
 * SECURITY NOTE: This is for offline development/testing purposes.
 * In production, Netlify Functions backend handles authentication
 * with credentials from environment variables.
 *
 * After first login in local mode, users should change this password
 * via the admin panel settings.
 */
// Hash of 'ShowGroom2024!' - a stronger default password for local dev
export const DEFAULT_ADMIN_HASH = 'd6211e973a91eeafe0cd76619c1cb9090204ceae15a38c8b28d24110610046fb';

export const DEFAULT_ADMIN: User = {
  id: 'admin-default',
  username: 'admin',
  email: 'admin@showgroom.local',
  passwordHash: DEFAULT_ADMIN_HASH,
  role: 'admin',
  createdAt: new Date().toISOString(),
};

// ==================== Utility Functions ====================

export function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
}

export async function hashPassword(password: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// ==================== Initialization ====================

// Singleton lock to prevent race conditions
let initializationPromise: Promise<void> | null = null;
let isInitialized = false;

export async function initializeDatabase(): Promise<void> {
  // If already initialized, return immediately
  if (isInitialized) {
    return;
  }
  
  // If initialization is in progress, wait for it
  if (initializationPromise) {
    return initializationPromise;
  }
  
  // Start initialization with a lock
  initializationPromise = (async () => {
    try {
      // Use a transaction to ensure atomicity
      await db.transaction('rw', [db.users, db.businessHours, db.settings, db.images], async () => {
        // Check if we need to initialize default data
        const usersCount = await db.users.count();

        if (usersCount === 0) {
          // Add default admin user using put (upsert) to be safe
          // Compute correct hash at runtime
          const correctHash = await hashPassword('ShowGroom2024!');
          await db.users.put({ ...DEFAULT_ADMIN, passwordHash: correctHash });
        } else {
          // Fix default admin hash if it's wrong (one-time repair)
          const admin = await db.users.get('admin-default');
          if (admin) {
            const correctHash = await hashPassword('ShowGroom2024!');
            if (admin.passwordHash !== correctHash) {
              await db.users.update('admin-default', { passwordHash: correctHash });
            }
          }
        }

        // Check if business hours exist - use put for upsert behavior
        const hoursDoc = await db.businessHours.get('singleton');
        if (!hoursDoc) {
          await db.businessHours.put({
            id: 'singleton',
            data: DEFAULT_BUSINESS_HOURS,
          });
        }

        // Initialize settings if not exist - use put for upsert behavior
        const settingsDoc = await db.settings.get('singleton');
        if (!settingsDoc) {
          await db.settings.put({
            id: 'singleton',
            migratedFromLocalStorage: false,
          });
        }
      });

      isInitialized = true;
    } catch (error) {
      // Reset the promise so retry is possible
      initializationPromise = null;
      throw error;
    }
  })();
  
  return initializationPromise;
}

// Note: We don't auto-initialize on import anymore.
// The ensureInitialized() function in dbBackend.ts handles this.